/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Send, Shield, Clock, MessageCircle, CheckCircle } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import axios from "axios";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GetQuotePage = () => {
  const [searchParams] = useSearchParams();
  const [travelDate, setTravelDate] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    destination: searchParams.get("destination") || "",
    travelers: "",
    budget: "",
    package_type: searchParams.get("package") ? "specific" : "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const leadData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        destination: formData.destination || null,
        travel_dates: travelDate ? format(travelDate, "PPP") : null,
        travelers: formData.travelers ? parseInt(formData.travelers) : null,
        budget: formData.budget || null,
        package_type: formData.package_type || null,
        message: formData.message || null,
        source: "get_quote"
      };
      await axios.post(`${API}/leads`, leadData);
      toast.success("Quote request submitted! Our team will contact you within 4 hours.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        destination: "",
        travelers: "",
        budget: "",
        package_type: "",
        message: "",
      });
      setTravelDate(null);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  const destinations = [
    "Dubai",
    "Thailand",
    "Maldives",
    "Singapore",
    "Switzerland",
    "Bali",
    "Goa",
    "Kerala",
    "Rajasthan",
    "Kashmir",
    "Ladakh",
    "Andaman",
    "Europe",
    "Australia",
    "Other",
  ];

  const budgetRanges = [
    "Under ₹25,000",
    "₹25,000 - ₹50,000",
    "₹50,000 - ₹1,00,000",
    "₹1,00,000 - ₹2,00,000",
    "Above ₹2,00,000",
    "Flexible",
  ];

  return (
    <div data-testid="get-quote-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-slate-900" data-testid="quote-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1920&q=80"
            alt="Get Quote"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-900" />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="caption text-coral mb-4 block">Plan Your Trip</span>
            <h1 className="heading-1 text-white mb-6">Get Your Free Quote</h1>
            <p className="text-lg text-white/80">
              Tell us about your dream vacation and our travel experts will craft 
              a personalized itinerary with the best deals just for you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="section-padding bg-accent" data-testid="quote-form-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Form */}
            <motion.div {...fadeInUp} className="lg:col-span-2">
              <Card className="shadow-xl" data-testid="quote-form-card">
                <CardContent className="p-8">
                  <h2 className="heading-3 text-primary mb-2">Trip Enquiry Form</h2>
                  <p className="text-slate-600 mb-8">
                    Fill in the details below and we'll get back to you with a customized quote.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Personal Info */}
                    <div>
                      <h3 className="font-heading font-semibold text-primary mb-4">
                        Personal Information
                      </h3>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Full Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="h-12"
                            placeholder="Your name"
                            data-testid="quote-input-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="h-12"
                            placeholder="your@email.com"
                            data-testid="quote-input-email"
                          />
                        </div>
                      </div>
                      <div className="mt-4">
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="h-12 mt-2"
                          placeholder="+91 98765 43210"
                          data-testid="quote-input-phone"
                        />
                      </div>
                    </div>

                    {/* Trip Details */}
                    <div className="pt-6 border-t border-slate-200">
                      <h3 className="font-heading font-semibold text-primary mb-4">
                        Trip Details
                      </h3>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Destination *</Label>
                          <Select
                            value={formData.destination}
                            onValueChange={(value) =>
                              setFormData({ ...formData, destination: value })
                            }
                          >
                            <SelectTrigger className="h-12" data-testid="quote-select-destination">
                              <SelectValue placeholder="Select destination" />
                            </SelectTrigger>
                            <SelectContent>
                              {destinations.map((dest) => (
                                <SelectItem key={dest} value={dest}>
                                  {dest}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Travel Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full h-12 justify-start text-left font-normal",
                                  !travelDate && "text-muted-foreground"
                                )}
                                data-testid="quote-date-picker"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {travelDate ? format(travelDate, "PPP") : "Pick a date"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={travelDate}
                                onSelect={setTravelDate}
                                disabled={(date) => date < new Date()}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4 mt-4">
                        <div className="space-y-2">
                          <Label htmlFor="travelers">Number of Travelers *</Label>
                          <Input
                            id="travelers"
                            name="travelers"
                            type="number"
                            min="1"
                            value={formData.travelers}
                            onChange={handleChange}
                            required
                            className="h-12"
                            placeholder="e.g., 2"
                            data-testid="quote-input-travelers"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Budget Range</Label>
                          <Select
                            value={formData.budget}
                            onValueChange={(value) =>
                              setFormData({ ...formData, budget: value })
                            }
                          >
                            <SelectTrigger className="h-12" data-testid="quote-select-budget">
                              <SelectValue placeholder="Select budget" />
                            </SelectTrigger>
                            <SelectContent>
                              {budgetRanges.map((range) => (
                                <SelectItem key={range} value={range}>
                                  {range}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="mt-4 space-y-2">
                        <Label>Package Type</Label>
                        <Select
                          value={formData.package_type}
                          onValueChange={(value) =>
                            setFormData({ ...formData, package_type: value })
                          }
                        >
                          <SelectTrigger className="h-12" data-testid="quote-select-type">
                            <SelectValue placeholder="Select package type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="honeymoon">Honeymoon</SelectItem>
                            <SelectItem value="family">Family Holiday</SelectItem>
                            <SelectItem value="group">Group Tour</SelectItem>
                            <SelectItem value="corporate">Corporate / MICE</SelectItem>
                            <SelectItem value="adventure">Adventure</SelectItem>
                            <SelectItem value="luxury">Luxury</SelectItem>
                            <SelectItem value="budget">Budget</SelectItem>
                            <SelectItem value="custom">Custom</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Additional Info */}
                    <div className="pt-6 border-t border-slate-200">
                      <div className="space-y-2">
                        <Label htmlFor="message">Additional Requirements</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={4}
                          placeholder="Tell us about your preferences, special requests, or any specific requirements..."
                          data-testid="quote-textarea-message"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="btn-coral w-full"
                      disabled={isSubmitting}
                      data-testid="quote-submit-btn"
                    >
                      {isSubmitting ? "Submitting..." : "Get Free Quote"}
                      <Send className="ml-2 h-5 w-5" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>

            {/* Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              {/* Why Us */}
              <Card className="shadow-lg" data-testid="quote-why-card">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-primary mb-4">
                    Why Get a Quote From Us?
                  </h3>
                  <div className="space-y-4">
                    {[
                      { icon: Clock, text: "Response within 4 hours" },
                      { icon: Shield, text: "100% free, no obligations" },
                      { icon: CheckCircle, text: "Best price guarantee" },
                      { icon: MessageCircle, text: "Expert travel advice" },
                    ].map((item, index) => (
                      <div key={`benefit-${item.text.slice(0, 15)}`} className="flex items-center gap-3">
                        <item.icon className="h-5 w-5 text-coral flex-shrink-0" />
                        <span className="text-slate-600 text-sm">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* WhatsApp CTA */}
              <Card className="bg-green-50 border-green-100" data-testid="quote-whatsapp-card">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-primary mb-1">
                        Prefer WhatsApp?
                      </h4>
                      <p className="text-slate-600 text-sm mb-3">
                        Get instant quotes on WhatsApp
                      </p>
                      <a
                        href="https://wa.me/918369033791?text=Hi, I'd like to get a quote for my trip"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button className="bg-green-500 hover:bg-green-600 text-white w-full" data-testid="quote-whatsapp-btn">
                          Chat on WhatsApp
                        </Button>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Trust Badge */}
              <Card className="shadow-lg" data-testid="quote-trust-card">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-bold text-coral mb-1">10,000+</div>
                  <p className="text-slate-600 text-sm mb-4">Happy Travelers Trust Us</p>
                  <div className="flex justify-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={`star-${i}`}
                        className="w-5 h-5 text-coral fill-current"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">4.9/5 average rating</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GetQuotePage;
